<script setup lang="ts">
import { validateNotEmpty, validateEmail } from '@mop/shared/utils/validationRules';
import { scrollToPosition } from '@mop/shared/utils/util';
import type { Ui2FormSelectItem, UiNotificationStatuses } from '@mop/ui2';
import { sessionStorageGet, sessionStorageSet } from '@mop/shared/utils/sessionStorage';
import type { Alternate, SeoBreadcrumb } from '@/types/cms';
import { isNumber } from '@/utils/util';

defineOptions({
  name: 'JobApply',
});

const emit = defineEmits(['page-not-found']);

const { $gtm, $mopI18n, $urls, $storyblokLivePreview } = useNuxtApp();
const classNameRef = ref(['']);
const contentPath = '/career/start-creating-with-us/our-jobs/apply';
const loadingRef = ref(false);
const route = useRoute();
const router = useRouter();
const { getCmsStory, trackPageView, cmsStoryModelRef, loadingRef: loadingCms } = useMopCms(contentPath);
const { initTransition } = useMopPageTransitionClient();
const {
  fetchJob,
  jobItemRef,
  sendApplication,
  s3UploadFile,
  getApplicationPresignedUrlsData,
  getJobIdFromPath,
  statusModelRef,
} = useMopJob(route.params.jobId as string);
const { getHeadObject, getAlternatesFromStory, setAlternates, getBreadcrumbSchema } = useMopSeo();
const jobId = getJobIdFromPath(route.params.jobId as string);

const maxSizeMB = 9.54; // in MB this is exactly 10,000,000 bytes.
const maxSize = 10_000_000; // made it lower to not reach actual 10MB but show nicer number to customer
const salaryExpectationMaxLength = 99;
const salaryNumberPattern = /\d+(?:[.,'’]\d+)*/g;
const salarySeparatorPattern = /[.,'’]/;

initTransition(classNameRef, loadingCms);

const notificationMessageRef = ref('');
const notificationTypeRef = ref<UiNotificationStatuses>('success');
const formEl = ref();
const formStateRef = ref({
  formData: {
    firstName: '',
    lastName: '',
    preferredName: '',
    email: '',
    phone: '',
    salaryExpectation: '',
    noticePeriod: '',
    source: '',
    sourceOther: '',
    files: undefined,
    additionalFiles: undefined,
    uploadedFiles: [],
  },
});

const sourceSelectItemsRef: Ref<Ui2FormSelectItem[]> = ref([]);
const sourceOtherLabelRef = ref('');

watch(
  () => cmsStoryModelRef.value,
  () => {
    if (!cmsStoryModelRef.value?.isInitialized()) {
      return;
    }
    const sourceList = cmsStoryModelRef.value?.getAttribute('sourceList') || [];
    sourceSelectItemsRef.value = [{ value: '', text: '-' }, ...sourceList];
    sourceOtherLabelRef.value = sourceList.find((sourceItem: any) => sourceItem.value === 'other')?.text;

    preSelectSourceFromReferrer(sourceList);
  },
  { immediate: true },
);

watch(
  () => formStateRef.value.formData.salaryExpectation,
  (newValue) => {
    formStateRef.value.formData.salaryExpectation = newValue
      .replace(salaryNumberPattern, formatSalaryNumber)
      .slice(0, salaryExpectationMaxLength);
  },
);

function formatSalaryNumber(salaryNumber: string) {
  const digitGroups = salaryNumber.split(salarySeparatorPattern);
  const isDecimalOrIncomplete = digitGroups.length > 1 && digitGroups[digitGroups.length - 1].length < 3;
  const hasLeadingZero = digitGroups.length === 1 && /^0\d/.test(salaryNumber);
  if (isDecimalOrIncomplete || hasLeadingZero) {
    return salaryNumber;
  }
  return new Intl.NumberFormat($mopI18n.locale || 'de').format(BigInt(digitGroups.join('')));
}

const rules = {
  validatePhone: (value: any) =>
    /[<>]/g.test(value) === false ||
    cmsStoryModelRef.value.getAttribute('errorPhone') ||
    $mopI18n.t('locale.validate.phone'),
  validateAlphabets: (value: any) =>
    /^[\p{L} \-']+$/giu.test(value.trim()) ||
    cmsStoryModelRef.value.getAttribute('errorNotValid') ||
    $mopI18n.t('locale.validate.not_valid'),
  validateAlphabetsNumbers: (value: any) =>
    /^[\p{L}0-9 \-']+$/giu.test(value.trim()) ||
    cmsStoryModelRef.value.getAttribute('errorNotValid') ||
    $mopI18n.t('locale.validate.not_valid'),
  validateNotEmpty: (value: any) =>
    validateNotEmpty(value) ||
    cmsStoryModelRef.value.getAttribute('errorRequired') ||
    $mopI18n.t('locale.validate.required'),
  validateEmail: (value: string) =>
    validateEmail(value) || cmsStoryModelRef.value.getAttribute('errorEmail') || $mopI18n.t('locale.validate.email'),
  validateFileCount: (value: FileList) => {
    if (!value?.length) {
      return cmsStoryModelRef.value.getAttribute('errorRequired') || $mopI18n.t('locale.validate.required');
    }
    return (
      value.length === 1 ||
      cmsStoryModelRef.value.getAttribute('errorMaxFileCount') ||
      $mopI18n.t('locale.validate.max_file_count')
    );
  },
  validateAdditionalFileCount: (value: FileList) => {
    return (
      !value ||
      value.length <= 10 ||
      cmsStoryModelRef.value.getAttribute('errorMaxFileCount') ||
      $mopI18n.t('locale.validate.max_file_count')
    );
  },
  validateCVFileSize: (value: FileList) => {
    if (!value) {
      return true;
    }
    const remainingSize = Array.from<File>(formStateRef.value.formData.files ?? []).reduce((remainingKB, file) => {
      remainingKB -= file.size;

      return remainingKB;
    }, maxSize);

    if (remainingSize <= 0) {
      return (
        cmsStoryModelRef.value.getAttribute('errorMaxFileSize').replace('{0}', `${maxSizeMB}MB`) ||
        $mopI18n.t('locale.validate.max_file_size', [`${maxSizeMB}MB`])
      );
    }
    return true;
  },
  validateOtherFileSize: (value: FileList) => {
    if (!value) {
      return true;
    }
    const remainingSizeFromCV = Array.from<File>(formStateRef.value.formData.files ?? []).reduce(
      (remainingKB, file) => {
        remainingKB -= file.size;

        return remainingKB;
      },
      maxSize,
    );

    const remainingSize = Array.from<File>(formStateRef.value.formData.additionalFiles ?? []).reduce(
      (remainingKB, file) => {
        remainingKB -= file.size;

        return remainingKB;
      },
      remainingSizeFromCV,
    );

    if (remainingSize <= 0) {
      if (remainingSizeFromCV < 0) {
        return (
          cmsStoryModelRef.value.getAttribute('errorMaxFileSize').replace('{0}', '') ||
          $mopI18n.t('locale.validate.max_file_size', [''])
        );
      }
      const remainingSizeFormatted =
        remainingSizeFromCV < 1024 * 1024
          ? (remainingSizeFromCV / 1024).toFixed(0) + 'KB'
          : (remainingSizeFromCV / 1024 / 1024).toFixed(1) + 'MB';
      return (
        cmsStoryModelRef.value.getAttribute('errorMaxFileSize').replace('{0}', remainingSizeFormatted) ||
        $mopI18n.t('locale.validate.max_file_size', [remainingSizeFormatted])
      );
    }
    return true;
  },
  validateFileType: (value: FileList) => {
    if (!value) {
      return true;
    }
    if (Array.from(value).some((file: File) => file.name.endsWith('pdf') === false)) {
      return cmsStoryModelRef.value.getAttribute('errorFileType') || $mopI18n.t('locale.validate.file_type');
    }
    return true;
  },
};

function preSelectSourceFromReferrer(sourceList: any[]) {
  // _ref query param overrides referrer for testing
  const refOverride = route.query._ref as string | undefined;
  let sourceKey = 'career-page';
  const url = refOverride ? `https://${refOverride}` : document.referrer;
  try {
    const hostname = new URL(url).hostname?.replace(/^www\./, '');
    const preSelectSource = sourceList.find((sourceItem: any) =>
      sourceItem.referrers?.some((referrer: string) => {
        if (hostname.includes(referrer)) {
          sourceKey = referrer;
          return true;
        }
        return false;
      }),
    )?.value;
    if (preSelectSource) {
      sessionStorageSet('applicationSource', sourceKey);

      formStateRef.value.formData.source = preSelectSource;
      if (preSelectSource === 'other') {
        formStateRef.value.formData.sourceOther = sourceKey;
      }
    }
  } catch (error) {}
}

async function handleSubmit() {
  loadingRef.value = true;
  formStateRef.value.formData.email = formStateRef.value.formData.email.trim();

  const payload = new FormData(formEl.value.$el);
  let source = formStateRef.value.formData.source;
  if (source === 'other') {
    source = formStateRef.value.formData.sourceOther;
  }
  if (!source) {
    source = sessionStorageGet('applicationSource') || 'career-page';
  }
  payload.append('vacancyId', jobId);
  payload.append('source', source);
  payload.delete('files-temp[]');
  payload.delete('additional-files-temp[]');
  payload.delete('source-other');

  const uploadFiles: {
    success?: boolean;
    fileName: string;
    file: File;
    uploadFileName?: string;
    uploadUrl?: string;
    fields?: Record<string, string>;
    url?: string;
    errorCode?: string;
  }[] = [];

  for (const file of formStateRef.value.formData.files! as FileList) {
    uploadFiles.push({
      fileName: file.name,
      file,
    });
  }

  if (formStateRef.value.formData?.additionalFiles) {
    for (const file of formStateRef.value.formData.additionalFiles as FileList) {
      uploadFiles.push({
        fileName: file.name,
        file,
      });
    }
  }

  const uploadFileNames = uploadFiles.map((uploadFile) => uploadFile.fileName);

  // TODO: get presigned urls for the files
  const presignedUrlsData = (await getApplicationPresignedUrlsData(uploadFileNames))?.data;
  if (presignedUrlsData) {
    uploadFiles.forEach((uploadFile) => {
      uploadFile.url = presignedUrlsData[uploadFile.fileName]?.url;
      uploadFile.fields = presignedUrlsData[uploadFile.fileName]?.fields;
    });
  }
  for (const uploadFile of uploadFiles) {
    const results = await s3UploadFile(uploadFile);
    uploadFile.success = !results?.error;
    uploadFile.errorCode = results?.error;
  }
  const allFilesSuccess = uploadFiles.every((uploadFile) => uploadFile.success);
  if (!allFilesSuccess) {
    const tranlationKey = `locale.externalJobErrors.${
      uploadFiles.filter((uploadFile) => !uploadFile.success)[0]?.errorCode
    }`;
    notificationMessageRef.value = $mopI18n.te(tranlationKey)
      ? $mopI18n.t(tranlationKey)
      : cmsStoryModelRef.value.getAttribute('errorSubmit') || $mopI18n.t('locale.jobs.post_error');
    notificationTypeRef.value = 'error';
    loadingRef.value = false;
    scrollToPosition(0);
    return;
  }

  uploadFiles.forEach((uploadFile) => {
    if (uploadFile.fields) {
      payload.append(`uploaded-files-keys[]`, uploadFile.fields.key);
    }
  });
  // payload.append('uploaded-files-keys', JSON.stringify(uploadFiles.map((file) => file.fields?.key)));
  await sendApplication(payload);
  const result: any = statusModelRef.value.getResult();
  if (!isNumber(result) || statusModelRef.value.hasError()) {
    const tranlationKey = `locale.externalJobErrors.${result}`;
    notificationMessageRef.value =
      result && $mopI18n.te(tranlationKey)
        ? $mopI18n.t(tranlationKey)
        : result || cmsStoryModelRef.value.getAttribute('errorSubmit') || $mopI18n.t('locale.jobs.post_error');
    notificationTypeRef.value = 'error';
    scrollToPosition(0);
  } else {
    $gtm.trackEvent({
      eventName: 'purchase',
      customValue: {
        affiliation: 'manual',
        transaction_id: String(result),
        items: [jobItemRef.value.getGtmTrackData()],
      },
    });
    await router.push($mopI18n.localePath(`${$urls.CAREER_THANK_YOU_PAGE}?jobId=${jobId}`));
  }
  loadingRef.value = false;
}

const metaData = computed(() => {
  const careerLink: string = $mopI18n.localePath($urls.CAREER);
  const startCreatingLink: string = $mopI18n.localePath($urls.CAREER_START_CREATING);
  const ourJobsLink: string = $mopI18n.localePath($urls.CAREER_PAGE);
  const title: string = jobItemRef.value.getTitle();
  const description: string = cmsStoryModelRef?.value.getSeo()?.description?.replace('{job-title}', title) || title;
  const isIndexable = true;
  const canonical = route.path;
  let alternates: Alternate[] = getAlternatesFromStory(cmsStoryModelRef.value);
  alternates = alternates.map((alternate: Alternate) => {
    const hrefSplit: string[] = alternate.href.split('/');
    const href = `${hrefSplit.join('/')}/${jobItemRef.value.getUrl()}`;
    return {
      href,
      lang: alternate.lang,
    };
  });
  setAlternates(alternates);

  const breadcrumbs: SeoBreadcrumb[] = [
    {
      name: $mopI18n.t('locale.blog.name_career'),
      url: careerLink,
    },
    {
      name: $mopI18n.t('locale.jobs.start_creating_with_us'),
      url: startCreatingLink,
    },
    {
      name: $mopI18n.t('locale.jobs.search_button'),
      url: ourJobsLink,
    },
    {
      name: title,
      url: route.path,
    },
  ];
  const ogDescription: string = jobItemRef.value.getDescription1();
  const ogImage: string = cmsStoryModelRef?.value.getAttribute('ogImage');
  const headObject = getHeadObject({
    title,
    description,
    isIndexable,
    canonical,
    alternates,
    ogDescription,
    ogImage,
  });

  return { ...headObject, ...getBreadcrumbSchema(breadcrumbs) };
});

useAsyncData(
  () => getAsyncDatatCacheId(route),
  async () => {
    const language = await fetchJob(jobId);
    if (!jobItemRef.value.getId() && !$storyblokLivePreview.isEnabledInIframe) {
      emit('page-not-found');
      return null;
    }
    await getCmsStory(contentPath, {
      language: language || 'default',
    });
    return null;
  },
);

onMounted(() => {
  if (!jobItemRef.value.getId() && !$storyblokLivePreview.isEnabledInIframe) {
    return;
  }
  trackPageView('Career');
  $gtm.trackEvent({
    eventName: 'begin_checkout',
    customValue: {
      items: [jobItemRef.value.getGtmTrackData()],
    },
  });
});

$storyblokLivePreview.initStoryListener('Storyblok', cmsStoryModelRef);

useHead(metaData as any);
</script>

<template>
  <div :class="['job-apply', 'transition', ...classNameRef]">
    <MopPageLoad :class="classNameRef" />

    <div v-if="jobItemRef.getId()" class="transition-content">
      <Ui2Notification
        v-if="notificationMessageRef"
        class="job-apply__notification"
        :status="notificationTypeRef"
        show-icon
      >
        {{ notificationMessageRef }}
      </Ui2Notification>

      <MopCmsContentElementsSlot
        id="job-apply__content-top"
        class="job-apply__content-top"
        no-padding
        :elements="cmsStoryModelRef.getContentElements('bodyTop')"
      />

      <h2 class="job-apply__subline">
        <NuxtLink
          class="job-apply__link-to-job"
          :to="$mopI18n.localePath(`${$urls.CAREER_PAGE}/${jobItemRef.getUrl()}`)"
          no-prefetch
        >
          {{ jobItemRef.getTitle() }}
        </NuxtLink>
      </h2>

      <Ui2Form v-if="jobId" ref="formEl" class="job-apply__form" long @submit="handleSubmit">
        <div class="job-apply__subline">
          {{ cmsStoryModelRef.getAttribute('titlePersonalData') }}
        </div>

        <Ui2TextFieldInput
          id="first-name"
          v-model="formStateRef.formData.firstName"
          name="first-name"
          :label="cmsStoryModelRef.getAttribute('firstName')"
          floating
          :rules="[rules.validateNotEmpty, rules.validateAlphabets]"
          required
          autocomplete="given-name"
        />

        <Ui2TextFieldInput
          id="last-name"
          v-model="formStateRef.formData.lastName"
          name="last-name"
          :label="cmsStoryModelRef.getAttribute('lastName')"
          floating
          :rules="[rules.validateNotEmpty, rules.validateAlphabets]"
          required
          autocomplete="family-name"
        />

        <Ui2TextFieldInput
          id="preferred-name"
          v-model="formStateRef.formData.preferredName"
          name="preferred-name"
          :label="cmsStoryModelRef.getAttribute('preferredName')"
          floating
          :rules="[(value: string) => (value ? rules.validateAlphabets(value) : true)]"
          optional
          autocomplete="preferred-name"
        />

        <Ui2TextFieldInput
          id="email"
          v-model="formStateRef.formData.email"
          name="email"
          type="email"
          :label="cmsStoryModelRef.getAttribute('email')"
          floating
          :rules="[rules.validateNotEmpty, rules.validateEmail]"
          required
          autocomplete="email"
        />

        <Ui2TextFieldInput
          id="phone"
          v-model="formStateRef.formData.phone"
          name="phone"
          type="phone"
          :label="cmsStoryModelRef.getAttribute('phone')"
          floating
          :rules="[rules.validateNotEmpty, rules.validatePhone]"
          required
          autocomplete="tel"
        />
        <Ui2TextFieldInput
          v-if="jobItemRef.hasSalaryExpectations()"
          id="salary-expectation"
          v-model="formStateRef.formData.salaryExpectation"
          name="salary-expectation"
          :label="
            jobItemRef.isHourlyWage()
              ? cmsStoryModelRef.getAttribute('salaryExpectationHourly')
              : cmsStoryModelRef.getAttribute('salaryExpectation')
          "
          floating
          optional
          :maxlength="salaryExpectationMaxLength"
          autocomplete="off"
        />

        <Ui2TextFieldInput
          v-if="jobItemRef.hasNoticePeriod()"
          id="notice-period"
          v-model="formStateRef.formData.noticePeriod"
          name="notice-period"
          :label="cmsStoryModelRef.getAttribute('noticePeriod')"
          floating
          :rules="[rules.validateNotEmpty, rules.validateAlphabetsNumbers]"
          required
          autocomplete="off"
        />

        <Ui2DropdownSelect
          id="source"
          v-model="formStateRef.formData.source"
          :items="sourceSelectItemsRef"
          :label="cmsStoryModelRef.getAttribute('source')"
          :placeholder="cmsStoryModelRef.getAttribute('source')"
          required
          floating
          :rules="[rules.validateNotEmpty]"
        />

        <Ui2TextFieldInput
          v-if="formStateRef.formData.source === 'other'"
          id="source-other"
          v-model="formStateRef.formData.sourceOther"
          name="source-other"
          :label="sourceOtherLabelRef"
          floating
          :rules="[rules.validateNotEmpty]"
          required
          autocomplete="off"
        />

        <div class="job-apply__form-group">
          <div class="job-apply__subline">
            {{ cmsStoryModelRef.getAttribute('documentLabel') }}
            <div class="job-apply__hint">
              <Ui2Icon name="info-circle" :width="20" :height="20" />
              {{ cmsStoryModelRef.getAttribute('documentHint') }}
            </div>
          </div>
          <Ui2Notification v-if="jobItemRef.isApprenticeship()" status="default" show-icon>
            {{ cmsStoryModelRef.getAttribute('uploadFileApprenticeshipNote') }}
          </Ui2Notification>

          <div class="job-apply__form-label">
            {{ cmsStoryModelRef.getAttribute('titleUploadFile') || $mopI18n.t('locale.jobs.upload_file') }}
          </div>
          <Ui2FileUpload
            id="files"
            v-model="formStateRef.formData.files"
            name="files-temp[]"
            :rules="[rules.validateFileCount, rules.validateFileType, rules.validateCVFileSize]"
            accept=".pdf"
            enable-pdf-preview
            :localisation="{
              click_to_upload: cmsStoryModelRef.getAttribute('uploadFileCta'),
              drag_content: cmsStoryModelRef.getAttribute('uploadFileCtaAdditional'),
              drag_document_here: cmsStoryModelRef.getAttribute('uploadFileCtaDrag'),
              unsupported_file: cmsStoryModelRef.getAttribute('uploadFileUnsupportedFile'),
            }"
          />
        </div>
        <div class="job-apply__form-group">
          <div class="job-apply__form-label">
            {{
              cmsStoryModelRef.getAttribute('titleUploadAdditionalFiles') ||
              $mopI18n.t('locale.jobs.upload_file_additional')
            }}
          </div>
          <Ui2FileUpload
            id="filesOther"
            v-model="formStateRef.formData.additionalFiles"
            name="additional-files-temp[]"
            :rules="[rules.validateAdditionalFileCount, rules.validateFileType, rules.validateOtherFileSize]"
            accept=".pdf"
            multiple
            enable-pdf-preview
            :localisation="{
              click_to_upload: cmsStoryModelRef.getAttribute('uploadFileCta'),
              drag_content: cmsStoryModelRef.getAttribute('uploadFileCtaAdditional'),
              drag_document_here: cmsStoryModelRef.getAttribute('uploadFileCtaDrag'),
              unsupported_file: cmsStoryModelRef.getAttribute('uploadFileUnsupportedFile'),
            }"
          >
            <template #hint>
              <div v-if="cmsStoryModelRef.getAttribute('uploadFileNote')" class="job-apply__hint">
                <Ui2Icon name="info-circle" :width="20" :height="20" />
                {{ cmsStoryModelRef.getAttribute('uploadFileNote')?.replace('{size}', `${maxSizeMB}MB`) }}
              </div>
            </template>
          </Ui2FileUpload>
        </div>
        <div class="job-apply__form-group">
          <Ui2Button
            :label="cmsStoryModelRef.getAttribute('cta') || $mopI18n.t('locale.jobs.submit')"
            :disabled="loadingRef"
            :loading="loadingRef"
            html-type="submit"
            size="lg"
          />
        </div>
        <div class="job-apply__form-group">
          <Ui2Button
            :label="cmsStoryModelRef.getAttribute('goBack') || $mopI18n.t('locale.jobs.go_back')"
            type="ghost"
            icon-start="arrow-left"
            html-type="link"
            :to="$mopI18n.localePath(`${$urls.CAREER_PAGE}/${jobItemRef.getUrl()}`)"
            size="lg"
            full-width
          />
        </div>
      </Ui2Form>

      <MopCmsContentElementsSlot
        id="job-apply__content-bottom"
        class="job-apply__content-bottom"
        no-padding
        :elements="cmsStoryModelRef.getContentElements('bodyBottom')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.job-apply {
  padding: 0 $global-padding;
  max-width: 470px;
  margin: 0 auto;
}
.job-apply__content-top {
  text-wrap: nowrap;

  @include v2-apply-upto('mobile') {
    text-wrap: wrap;
  }
}

.job-apply__form {
  display: flex;
  flex-direction: column;
  gap: $space-12;
  margin-top: $space-24;
}

.job-apply__subline {
  @include v2-text-style(md, highlight);
  padding: 0;
  margin: 0;
}

.job-apply__hint {
  @include v2-text-style(sm, default);
  display: flex;
  gap: $space-4;
  color: $color-text-body-tertiary;
  padding-top: $space-8;
}

.job-apply__form-group {
  display: flex;
  flex-direction: column;
  gap: $space-12;
  margin-top: $space-12;
}

.job-apply__form-label {
  @include v2-text-style(sm, highlight);
}

.job-apply__link-to-job {
  @include v2-text-style(lg, highlight);
}

.job-apply__notification {
  margin: 0 $global-padding;
}

.job-apply__content-bottom {
  margin: $space30 0;
}
</style>
